import { css } from 'aphrodite';
import React, { useRef } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { styleSheet } from './styles';

interface ColorPickerProps {
	value: string;
	onChange: (color: string) => void;
	ariaLabelledBy?: string;
}

function getContrastTextColor(hexBg: string): string {
	const shorthand = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hexBg);
	if (shorthand) {
		hexBg = '#' + shorthand[1] + shorthand[1] + shorthand[2] + shorthand[2] + shorthand[3] + shorthand[3];
	}
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexBg);
	if (!result) return '#ffffff';
	const channels = [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].map(c => {
		c = c / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
	return luminance > 0.179 ? '#000000' : '#ffffff';
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange, ariaLabelledBy }) => {
	const contentRef = useRef<HTMLDivElement>(null);

	const normalizedValue = value?.toLowerCase() || '#000000';
	const overlayColor = getContrastTextColor(normalizedValue);
	// react-colorful requires a defined color; the swatch below may render before a value is set.
	const safeValue = value || '#000000';

	return (
		<div className={css(styleSheet.container)}>
			<Popover>
				<PopoverTrigger asChild>
					<button
						type='button'
						className={css(styleSheet.colorBox)}
						style={{ backgroundColor: normalizedValue }}
						aria-label='Open color picker'
						aria-labelledby={ariaLabelledBy}
					>
						<span className={css(styleSheet.hexOverlay)} style={{ color: overlayColor }}>
							{normalizedValue.toUpperCase()}
						</span>
					</button>
				</PopoverTrigger>
				<PopoverContent
					ref={contentRef}
					side='bottom'
					align='start'
					sideOffset={8}
					avoidCollisions
					styles={[styleSheet.popover]}
					onOpenAutoFocus={event => {
						event.preventDefault();
						contentRef.current?.querySelector<HTMLInputElement>('#hex-input')?.focus();
					}}
				>
					<div className={css(styleSheet.popoverContainer)}>
						<HexColorPicker color={safeValue} onChange={onChange} />
						<div>
							<label className={css(styleSheet.inputLabel)} htmlFor='hex-input'>
								Hex
							</label>
							<HexColorInput
								id='hex-input'
								color={safeValue.toUpperCase()}
								onChange={onChange}
								className={css(styleSheet.hexInput)}
								prefixed
								aria-label='Hex color input'
							/>
						</div>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export { ColorPicker };
